import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { abacOperationMiddleware } from '../../../permissions/middlewares/abacOperationMiddleware.js';
import { checkPermissionMiddleware } from '../../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../../permissions/roles.js';
import { Person } from '../../entities/person.js';
import { resolveSendSuSWaitlistUpdateLinks } from '../../../sus-entry-form/school-selection/server/resolveSendSuSUpdateLink.js';
import { resolveExportEdooSys } from '../../export/server/resolveExportEdooSys.js';
import { UserListSelectionInterface } from '../entities/UserListSelectionInterface.js';
import {
  resolveGetUserCountByListSelection,
  resolveGetUsersByListSelection,
} from '../server/resolvers/resolveGetUsersByListSelection.js';
import { resolveSubscribeUsersPaginatedFiltered } from '../server/resolvers/resolveGetUsersPaginatedFiltered.js';
import { resolveMassDeleteUsersForListSelection } from '../server/resolvers/resolveMassDeleteUsersForListSelection.js';
import { UserListActions } from '../UserListPolicy.js';

export const massDeleteUsersForListSelection = registerMutation({
  operationName: 'massDeleteUsersForListSelection',
  type: () => Int,
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  middlewares: [checkPermissionMiddleware(Permission.MASS_USER_DELETION, GLOBAL_CONTEXT)],
  resolve: resolveMassDeleteUsersForListSelection,
});

const subscribeUsersPaginatedFiltered = registerSubscription({
  operationName: 'subscribeUsersPaginatedFiltered',
  type: () => [Person],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.VIEW_PERSONS)],
  resolve: resolveSubscribeUsersPaginatedFiltered,
});

export const getUsersByListSelection = registerQuery({
  operationName: 'getUsersByListSelection',
  type: () => [Person],
  params: [
    { type: () => UserListSelectionInterface, name: 'listSelection' },
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.VIEW_PERSONS)],
  resolve: resolveGetUsersByListSelection,
});

export const previewUserListSelection = registerMutation({
  operationName: 'previewUserListSelection',
  type: () => Int,
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  middlewares: [checkPermissionMiddleware(Permission.VIEW_PERSONS)],
  resolve: resolveGetUserCountByListSelection,
});

const exportEdooSys = registerQuery({
  operationName: 'exportEdooSys',
  type: () => String,
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  middlewares: [abacOperationMiddleware(UserListActions.USER_LIST_EXPORT_EDOO_SYS)],
  resolve: resolveExportEdooSys,
});

/** Mails the guardians of the selected waiting applicants their update link. Returns the number of mails. */
const sendSuSWaitlistUpdateLinks = registerMutation({
  operationName: 'sendSuSWaitlistUpdateLinks',
  type: () => Int,
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  middlewares: [abacOperationMiddleware(UserListActions.USER_LIST_SEND_SUS_WAITLIST_UPDATE_LINKS)],
  resolve: resolveSendSuSWaitlistUpdateLinks,
});

/**
 * This API handles everything related to listing, searching and selecting users.
 */
export const UserListAPI = {
  getBySelection: getUsersByListSelection,
  previewSelection: previewUserListSelection,
  subscribe: subscribeUsersPaginatedFiltered,
  deleteBySelection: massDeleteUsersForListSelection,
  exportEdooSys,
  sendSuSWaitlistUpdateLinks,
};
